import type { z } from "zod";

/**
 * The competitions API wraps list responses in a 207-style envelope —
 * `{ results: [...], errors: [...] }` — rather than returning a bare array.
 * Extract the results array, defensively falling back to the raw payload if
 * it's already a plain array (keeps this safe if the shape ever changes).
 */
export function extractResults(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const envelope = raw as { results?: unknown; result?: unknown };
    if (Array.isArray(envelope.results)) return envelope.results;
    if (Array.isArray(envelope.result)) return envelope.result;
  }
  return [];
}

/**
 * Parse each item independently so one malformed entry doesn't drop the entire
 * list — mirrors the legacy `useIndividualValidation` behavior. Returns the
 * valid items plus how many were dropped, so a caller can surface a
 * degraded-data notice instead of silently showing a short list.
 */
export function parseEnvelopeListWithMeta<T>(
  raw: unknown,
  schema: z.ZodType<T>,
): { items: T[]; invalidCount: number } {
  const all = extractResults(raw);
  const items: T[] = [];
  for (const item of all) {
    const result = schema.safeParse(item);
    if (result.success) {
      items.push(result.data);
    } else {
      console.warn(
        "Dropping invalid competitions API item:",
        result.error,
        item,
      );
    }
  }
  return { items, invalidCount: all.length - items.length };
}

/** Valid items only — for lists where a dropped-count isn't surfaced to the UI. */
export function parseEnvelopeList<T>(raw: unknown, schema: z.ZodType<T>): T[] {
  return parseEnvelopeListWithMeta(raw, schema).items;
}
